import React from 'react';
import { format, addDays } from 'date-fns';
import { formatTime } from '../../utils/clock';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, CalendarCheck, CalendarX, MessageSquare, Clock } from 'lucide-react';
import CommonLoader from '../CommonLoader';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import { booking as calender_empty, selectDateTime as noslotts } from '../../assets/img/illustrations';
import { emptyDayLabel, emptyDayHint } from '../../utils/slotDayReason';

// Per-row translated <option> wrapper. Form value stays the specialty name so the
// existing match-by-name logic keeps working — only the display label is localised.
const SpecialityOption = ({ name, doctor }) => {
    const specialtyId = doctor?.appointmentSpeciality?.find((s) => s.name === name)?.specialtyId;
    const label = useMasterLabel('masterSpecialty', specialtyId, 'name', name);
    return <option value={name}>{label}</option>;
};

const MyDoctorMode = ({
    // State
    selectedDoctorId,
    selectedSpeciality,
    selectedAddressId,
    selectedAppointmentType,
    weekOffset,
    openDay,
    fieldErrors,
    isLoading,
    availableSlots,
    selectedDoctor,
    selectedAppointmentDetails,
    
    // Setters
    setSelectedDoctorId,
    setSelectedSpeciality,
    setSelectedAddressId,
    setSelectedAppointmentType,
    setWeekOffset,
    setOpenDay,
    
    // Handlers
    handleSlotClick,
    handlePrevWeek,
    handleNextWeek,
    toggleDayDrawer,
    formatDurationToMinutes,
    setFieldErrors,
    
    // Data
    doctorNameListByPatient,
    loadingList,
    isRescheduling,
    loading,
    specialities,
    addressTypes,
    appointmentTypes,
    today,
    
    // Translation
    t
}) => {
    const getCurrentWeekMonday = (date) => {
        const day = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(date.getFullYear(), date.getMonth(), diff);
    };

    const currentWeekMonday = getCurrentWeekMonday(today);
    const startDate = addDays(currentWeekMonday, weekOffset * 7);
    const endDate = addDays(startDate, 6);

    const availableDays = Object.entries(availableSlots || {})
        .map(([day, data]) => ({ day, ...data }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Every day refused with the same 'closed': no active week rule covers this
    // week, which is the one cause nothing in the app used to name (B7).
    const weekNotOpen = availableDays.length > 0 && availableDays.every((d) => d.reason === 'closed');

    return (
        <>
            {/* My Doctor Mode Filters */}
            <div data-testid="appointment-filters" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-3">
                <div data-testid="doctor-filter">
                    <label data-testid="doctor-label" className="block text-sm font-medium text-gray-700 mb-2">{t('patientappointment.select_doctor', 'Select Doctor')} <span className="text-red-500">*</span></label>
                    <select
                        data-testid="doctor-select"
                        value={selectedDoctorId}
                            onChange={(e) => {
                                setSelectedDoctorId(e.target.value);
                                setSelectedSpeciality('');
                                setSelectedAddressId('');
                                setSelectedAppointmentType('');
                                setWeekOffset(0);
                                setOpenDay(null);
                                if (fieldErrors.doctor) {
                                    setFieldErrors(prev => ({ ...prev, doctor: '' }));
                                }
                            }}
                        className={`tt-select w-full p-3 border rounded-xl bg-white text-gray-700 focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none transition-all duration-300 text-sm ${
                            fieldErrors.doctor ? 'border-red-500 bg-red-50' : 'border-gray-200'
                        }`}
                        disabled={isRescheduling || loadingList || (isRescheduling && loading)}
                    >
                        <option value="" disabled>{loadingList ? t('patientappointment.loading_doctors', 'Loading doctors...') : t('patientappointment.select_doctor', 'Select Doctor')}</option>
                        {doctorNameListByPatient && doctorNameListByPatient.length > 0 ? (
                            doctorNameListByPatient.map((doctor) => {
                                const isAvailable = doctor.available !== false;
                                const doctorName = doctor.name || `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim() || t('patientappointment.doctor_fallback', 'Doctor');
                                return (
                                    <option 
                                        key={doctor.doctorId || doctor._id} 
                                        value={doctor.doctorId || doctor._id}
                                        disabled={!isAvailable}
                                    >
                                        {doctorName}{!isAvailable ? ` (${t('patientappointment.not_available', 'Not Available')})` : ''}
                                    </option>
                                );
                            })
                        ) : (
                            !loadingList && <option value="" disabled>{t('patientappointment.no_doctors_available', 'No doctors available')}</option>
                        )}
                    </select>
                    {fieldErrors.doctor && <p data-testid="doctor-error" className="text-red-500 text-xs mt-1">{fieldErrors.doctor}</p>}
                </div>
                <div data-testid="speciality-filter">
                    <label data-testid="speciality-label" className="block text-sm font-medium text-gray-700 mb-2">{t('patientappointment.speciality_label')} <span className="text-red-500">*</span></label>
                    <select
                        data-testid="speciality-select"
                        value={selectedSpeciality}
                            onChange={(e) => {
                                setSelectedSpeciality(e.target.value);
                                setSelectedAddressId('');
                                setSelectedAppointmentType('');
                                setWeekOffset(0);
                                setOpenDay(null);
                                if (fieldErrors.speciality) {
                                    setFieldErrors(prev => ({ ...prev, speciality: '' }));
                                }
                            }}
                        className={`tt-select w-full p-3 border rounded-xl bg-white text-gray-700 focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none transition-all duration-300 text-sm ${
                            fieldErrors.speciality ? 'border-red-500 bg-red-50' : 'border-gray-200'
                        }`}
                        disabled={!selectedDoctorId || isRescheduling || (isRescheduling && loading)}
                    >
                        <option value="" disabled>{t('patientappointment.select_speciality_placeholder')}</option>
                        {specialities.map((spec) => (
                            <SpecialityOption key={spec} name={spec} doctor={selectedDoctor} />
                        ))}
                    </select>
                    {fieldErrors.speciality && <p data-testid="speciality-error" className="text-red-500 text-xs mt-1">{fieldErrors.speciality}</p>}
                </div>
                <div data-testid="address-type-filter">
                    <label data-testid="address-type-label" className="block text-sm font-medium text-gray-700 mb-2">{t('patientappointment.address_type_label')} <span className="text-red-500">*</span></label>
                    <select
                        data-testid="address-type-select"
                        value={selectedAddressId}
                        onChange={(e) => {
                            setSelectedAddressId(e.target.value);
                            setSelectedAppointmentType('');
                            setWeekOffset(0);
                            setOpenDay(null);
                            if (fieldErrors.address) {
                                setFieldErrors(prev => ({ ...prev, address: '' }));
                            }
                        }}
                        className={`tt-select w-full p-3 border rounded-xl bg-white text-gray-700 focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none transition-all duration-300 text-sm ${
                            fieldErrors.address ? 'border-red-500 bg-red-50' : 'border-gray-200'
                        }`}
                        disabled={!selectedDoctorId || !selectedSpeciality || isRescheduling || (isRescheduling && loading)}
                    >
                        <option value="" disabled>{t('patientappointment.select_address_type_placeholder')}</option>
                        {addressTypes.map((addr) => (
                            <option key={addr.id} data-testid={`address-type-option-${addr.id}`} value={addr.id}>{addr.type}</option>
                        ))}
                    </select>
                    {fieldErrors.address && <p data-testid="address-error" className="text-red-500 text-xs mt-1">{fieldErrors.address}</p>}
                </div>
                <div data-testid="appointment-type-filter">
                    <label data-testid="appointment-type-label" className="block text-sm font-medium text-gray-700 mb-2">{t('patientappointment.appointment_type_label')} <span className="text-red-500">*</span></label>
                    <select
                        data-testid="appointment-type-select"
                        value={selectedAppointmentType}
                        onChange={(e) => {
                            setSelectedAppointmentType(e.target.value);
                            setWeekOffset(0);
                            setOpenDay(null);
                            if (fieldErrors.appointmentType) {
                                setFieldErrors(prev => ({ ...prev, appointmentType: '' }));
                            }
                        }}
                        className={`tt-select w-full p-3 border rounded-xl bg-white text-gray-700 focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none transition-all duration-300 text-sm ${
                            fieldErrors.appointmentType ? 'border-red-500 bg-red-50' : 'border-gray-200'
                        }`}
                        disabled={!selectedDoctorId || !selectedSpeciality || !selectedAddressId || isRescheduling || (isRescheduling && loading)}
                    >
                        <option value="" disabled>{t('patientappointment.select_appointment_placeholder')}</option>
                        {appointmentTypes.map((type) => (
                            <option key={type} data-testid={`appointment-type-option-${type}`} value={type}>{type}</option>
                        ))}
                    </select>
                    {fieldErrors.appointmentType && <p data-testid="appointment-type-error" className="text-red-500 text-xs mt-1">{fieldErrors.appointmentType}</p>}
                </div>
            </div>

            {/* Appointment Details */}
            {selectedAppointmentType && (
                <div data-testid="appointment-details" className="mb-6 flex gap-4">
                    {/* DURATION */}
                    <div data-testid="appointment-duration-display" className="flex gap-3 items-center px-4 py-2 w-fit bg-white shadow-sm border border-[#4ABDBD]/20 rounded-xl">
                        <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[#4ABDBD]/15 text-[#3A9B9B]">
                            <Clock size={18} />
                        </span>
                        <div className="flex flex-col">
                            <p className="text-xs text-gray-400">{t('patientappointment.duration', 'Duration')}</p>
                            <p data-testid="appointment-duration-value" className="text-sm font-semibold text-gray-700">
                                {formatDurationToMinutes(selectedAppointmentDetails?.duration) || t('patientappointment.na')}
                            </p>
                        </div>
                    </div>

                    {/* MODE */}
                    <div data-testid="appointment-mode-display" className="flex gap-3 items-center px-4 py-2 w-fit bg-white shadow-sm border border-[#4ABDBD]/20 rounded-xl">
                        <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[#4ABDBD]/15 text-[#3A9B9B]">
                            <MessageSquare size={18} />
                        </span>
                        <div className="flex flex-col">
                            <p className="text-xs text-gray-400">{t('patientappointment.mode', 'Mode')}</p>
                            <p data-testid="appointment-mode-value" className="text-sm font-semibold text-gray-700">
                                {selectedAppointmentDetails?.virtual
                                    ? t('patientappointment.onsite_visio')
                                    : t('patientappointment.onsite')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Available Slots */}
            {isLoading ? (
                <div data-testid="my-doctor-mode-loading" className="flex flex-col items-center justify-center py-4 min-h-[200px]">
                    <CommonLoader />
                    <span data-testid="my-doctor-mode-loading-text" className="text-xl text-[#4ABDBD] font-semibold mt-4">{t('patientappointment.loading')}</span>
                </div>
            ) : selectedSpeciality && selectedAddressId && selectedAppointmentType && availableDays.length > 0 ? (
                <div data-testid="my-doctor-mode-slots-container">
                    <div className="flex items-center justify-center gap-4 sm:gap-6 mb-6">
                        <button
                            data-testid="my-doctor-mode-prev-week-button"
                            onClick={handlePrevWeek}
                            disabled={weekOffset === 0}
                            className="p-2 bg-[#4ABDBD] text-white rounded-[16px] disabled:opacity-50 disabled:cursor-not-allowed shadow hover:bg-[#3AA8A8] transition-all duration-300 cursor-pointer"
                            aria-label={t('patientappointment.previous_week_aria')}
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <h3 data-testid="my-doctor-mode-week-range" className="text-lg font-semibold text-gray-800">
                            {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
                        </h3>
                        <button
                            data-testid="my-doctor-mode-next-week-button"
                            onClick={handleNextWeek}
                            disabled={weekOffset === 2}
                            className="p-2 bg-[#4ABDBD] text-white rounded-[16px] disabled:opacity-50 disabled:cursor-not-allowed shadow hover:bg-[#3AA8A8] transition-all duration-300 cursor-pointer"
                            aria-label={t('patientappointment.next_week_aria')}
                        >
                            <ArrowRight size={20} />
                        </button>
                    </div>
                    {weekNotOpen && (
                        <div
                            data-testid="my-doctor-mode-week-not-open"
                            className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                        >
                            {t('patientappointment.week_not_open', 'This week is not open for booking — the doctor has no active week rule covering it. Open the week under the doctor’s availability, or page to another week.')}
                        </div>
                    )}
                    <div data-testid="my-doctor-mode-days-list" className="grid grid-cols-1 gap-4">
                        {availableDays.map(({ day, date, slots, reason }) => {
                            const hasAvailableSlots = slots.filter(slot => slot.status === 'available').length > 0;
                            // The API's per-day `reason` says WHY the day is empty
                            // ('closed' = the week was never opened for booking).
                            const emptyLabel = hasAvailableSlots ? '' : emptyDayLabel(reason, date, t);
                            const emptyHint = hasAvailableSlots ? '' : emptyDayHint(reason, t);
                            return (
                                <div
                                    key={day}
                                    data-testid={`my-doctor-mode-day-${day}`}
                                    className="group overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm hover:shadow-[0_0px_8px_0_#4ABDBD] transition-all duration-300"
                                >
                                    <div
                                        data-testid={`my-doctor-mode-day-header-${day}`}
                                        className={`p-4 bg-gray-50 flex items-center gap-3 ${hasAvailableSlots ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'} transition-all duration-200`}
                                        onClick={hasAvailableSlots ? () => toggleDayDrawer(day) : undefined}
                                    >
                                        {hasAvailableSlots ? (
                                            <CalendarCheck size={20} className="text-[#4ABDBD]" aria-label={t('patientappointment.available_slots_icon_alt')} />
                                        ) : (
                                            <CalendarX size={20} className="text-gray-400" aria-label={t('patientappointment.no_slots_icon_alt')} />
                                        )}
                                        <h4 data-testid={`my-doctor-mode-day-title-${day}`} className="font-semibold text-gray-500 group-hover:text-[#4ABDBD] transition-colors truncate">
                                            {t(`patientappointment.days.${day}`)}, {format(new Date(date), 'MMM d, yyyy')}
                                        </h4>
                                        {!hasAvailableSlots && (
                                            <span
                                                data-testid={`my-doctor-mode-day-no-slots-${day}`}
                                                data-slot-reason={reason || 'unknown'}
                                                title={emptyHint || undefined}
                                                className="text-sm text-gray-500 ml-auto"
                                            >
                                                {emptyLabel}
                                            </span>
                                        )}
                                    </div>
                                    {hasAvailableSlots && openDay === day && (
                                        <div data-testid={`my-doctor-mode-day-slots-${day}`} className="p-4 bg-white border-t border-gray-200">
                                            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                                                {slots
                                                    .filter(slot => slot.status === 'available')
                                                    .map((slot, idx) => (
                                                        <div
                                                            key={idx}
                                                            data-testid={`my-doctor-mode-slot-${day}-${idx}`}
                                                            className="p-2 bg-gray-50 border border-gray-200 rounded-xl text-center text-sm hover:bg-[#4ABDBD] hover:text-white cursor-pointer transition-all duration-200"
                                                            onClick={() => handleSlotClick(slot)}
                                                        >
                                                            {formatTime(slot.start)}
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div data-testid="my-doctor-mode-empty-state" className="text-center text-sm text-gray-500 mt-6">
                    {(!availableSlots && availableDays.length === 0) ? (
                        <div data-testid="my-doctor-mode-no-slots-state" className="flex flex-col items-center justify-center col-span-full min-h-[200px] px-4 sm:px-6 lg:px-8">
                            <img
                                src={noslotts}
                                alt={t('patientappointment.no_slots_image_alt')}
                                className="w-36 h-36 sm:w-44 sm:h-44 md:w-60 md:h-60 lg:w-88 lg:h-88 object-contain"
                            />
                            <p data-testid="my-doctor-mode-no-slots-text" className="text-gray-500 text-center text-sm sm:text-base md:text-lg">
                                {t('patientappointment.no_slots_for_criteria')}
                            </p>
                        </div>
                    ) : (
                        <div data-testid="my-doctor-mode-select-fields-state" className="flex flex-col items-center justify-center py-8 min-h-[200px]">
                        <img
                            src={calender_empty}
                            alt={t('patientappointment.select_specialty_date', 'Select specialty and date')}
                            className="w-36 h-36 sm:w-44 sm:h-44 md:w-60 md:h-60 object-contain"
                        />
                        <p data-testid="my-doctor-mode-select-fields-text" className="text-gray-500 text-center text-sm sm:text-base md:text-lg mt-4">
                        {t('patientappointment.select_all_fields')}
                        </p>
                    </div>
                    )}
                </div>
            )}
        </>
    );
};

export default MyDoctorMode;

